import identity from 'transmute/identity';
import FormattedMessage from 'I18n/components/FormattedMessage';
import hubHttpClient from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import { useCallback, useContext, useMemo } from 'react';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import { insertLink } from '../../utils/documentUtils';
import ContentContainer from '../../components/insertMenu/ContentContainer';
import { Tracker } from '../../../utils/usageTracker';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { getGlobalVars } from '../../utils/configUtils';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import { CLICK_MANAGE_INVOICES, INSERT_INVOICE } from 'rich-text-lib/constants/usageTracking';
import { PM_INVOICE } from '../../constants/events';
import InvoicesPopoverEmptyState from './InvoicesPopoverEmptyState';
import InvoicesPopoverErrorState from './InvoicesPopoverErrorState';
import transformInvoicesData from './transformInvoicesData';
import * as InvoicesApi from './InvoicesApi';
import UITextToolbarIcon from 'rich-text-lib/components/editor/UITextToolbarIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import { jsx as _jsx } from "react/jsx-runtime";
const InvoicesButton = ({
  onClick
}) => /*#__PURE__*/_jsx(UITooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertInvoice"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    "data-test-id": "invoices",
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(UITextToolbarIcon, {
      name: "invoice"
    })
  })
});
const trackManage = () => Tracker.track(PM_INVOICE, {
  action: CLICK_MANAGE_INVOICES
});
const trackInsert = () => Tracker.track(PM_INVOICE, {
  action: INSERT_INVOICE
});
export const getInvoicesUrl = () => `${getFullUrl('app')}/contacts/${PortalIdParser.get()}/objects/0-53/views/all/list`;
export default function InsertInvoice({
  outputType = contentOutputs.RENDERED,
  useContentContainer,
  onViewInsertMenu = () => {},
  onInsertComplete = () => {}
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertInvoice = useCallback(({
    htmlBody
  }) => {
    const tr = insertLink({
      htmlBody,
      editorState,
      to: editorState.selection.to,
      from: editorState.selection.from
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(editorState.apply(tr), plaintextOptions) : editorState.apply(tr));
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete]);
  const InvoicePopover = useMemo(() => {
    const apiFunctions = InvoicesApi.defaultApiWrapper(hubHttpClient);
    const insertInvoiceDecoratorConfig = {
      outputType,
      apiFunctions,
      mergeTagOptions: {
        includeCustomTokens: true
      }
    };
    if (useContentContainer) {
      return InsertContentDecorator(ContentContainer({
        onClickManageButton: trackManage,
        onInsertContent: trackInsert,
        portalId: PortalIdParser.get(),
        PopoverErrorState: InvoicesPopoverErrorState,
        PopoverEmptyState: InvoicesPopoverEmptyState,
        Header: GenericPopoverHeader,
        searchPlaceHolder: 'hubspot-prosemirror.invoices.popover.search.placeholder',
        transformData: transformInvoicesData,
        search: apiFunctions.searchInvoices,
        onViewInsertMenu,
        manageUrl: getInvoicesUrl(),
        titleMessage: 'hubspot-prosemirror.invoices.popover.header.insertInvoice'
      }), insertInvoiceDecoratorConfig);
    }
    return InsertContentDecorator(ContentButton({
      onInsertContent: trackInsert,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button: InvoicesButton,
      PopoverErrorState: InvoicesPopoverErrorState,
      PopoverEmptyState: InvoicesPopoverEmptyState,
      PopoverComponent: UIPopover,
      onOpenPopover: identity,
      Header: GenericPopoverHeader,
      popoverClassName: 'prosemirror-invoices-popover',
      searchPlaceHolder: 'hubspot-prosemirror.invoices.popover.search.placeholder',
      transformData: transformInvoicesData,
      search: apiFunctions.searchInvoices,
      popoverTestKey: 'invoices-popover',
      manageUrl: getInvoicesUrl(),
      titleMessage: 'hubspot-prosemirror.invoices.popover.header.insertInvoice'
    }), insertInvoiceDecoratorConfig);
  }, [outputType, onViewInsertMenu, useContentContainer]);
  return /*#__PURE__*/_jsx(InvoicePopover, {
    onChange: insertInvoice,
    useProsemirror: true
  });
}